'use client';
import dynamic from 'next/dynamic';
import useBeforeUnload from '@/hooks/useBeforeUnload';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider } from 'next-auth/react';
import { getQueryClient } from '@/queryClient';
import BackendService from '@/services/backendService';
import { configureConfig } from '@/config';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useDetectBrowserTranslation } from '@/hooks/useDetectBrowserTranslation';
import { Telemetry } from '@/hooks/useTelemetry';
import { noteAutoReloadRecovered } from '@/utils/crashReport';
import { maybeResetForCuripodEmbed } from '@/utils/embedReset';

// The import succeeding is how we know an auto-reload recovered: the
// ErrorBoundary only reloads for a failed chunk, and this callback runs
// once that chunk has evaluated. A rejection never gets here, so a reload
// that lands back on the crash screen does not emit a recovery.
const GameButler = dynamic(
  () =>
    import('../components/GameButler').then((mod) => {
      noteAutoReloadRecovered();
      return mod;
    }),
  { ssr: false },
);

const App = () => {
  useBeforeUnload();
  const { config } = configureConfig();
  // Synchronously (before the lazy GameButler mounts and reads the cached
  // participant) wipe Curipod-owned state when embedded in a Curipod iframe, so
  // every simulator reload starts fresh instead of reusing a dead session.
  useState(() => {
    maybeResetForCuripodEmbed();
    return null;
  });
  useEffect(() => {
    const LocalstorageService = require('@/LocalstorageService').default;
    const IndexedDBStorageService =
      require('@/IndexedDBStorageService').default;

    // Remove any old curipod localstorage cache
    const clearStaleCache = async () => {
      LocalstorageService.clearStaleCache();
      await IndexedDBStorageService.clearStaleCache();
    };
    clearStaleCache();
  }, []);
  const { browserTranslation } = useDetectBrowserTranslation();
  useEffect(() => {
    if (browserTranslation) {
      Telemetry()?.setCustomAttribute('browserTranslation', browserTranslation);
    }
  }, [browserTranslation]);

  useState(() => {
    const bService = new BackendService();
    bService.init(config.backend.coreApiUrl);

    return bService;
  });
  const queryClient = getQueryClient();
  return (
    <ErrorBoundary>
      {/* Student SSO identity. Carries no Curipod tokens — the participant JWT
          comes from the v1 join flow and lives in HttpService, so there is nothing
          here to keep refreshed. Window-focus refetch is off to avoid a session
          request every time a student tabs back into the lesson. */}
      <SessionProvider refetchOnWindowFocus={false}>
        <QueryClientProvider client={queryClient}>
          <AppContainer>
            <GameButler />
          </AppContainer>
        </QueryClientProvider>
      </SessionProvider>
    </ErrorBoundary>
  );
};

const AppContainer = styled.div`
  height: 100%;
  width: 100%;
`;

export default App;
